import React from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import EyeOpen from './icons/EyeOpen';
import EyeSlash from './icons/EyeSlash';

export type PasswordStrength = 'empty' | 'weak' | 'medium' | 'strong';

export const getPasswordStrength = (password: string): PasswordStrength => {
  if (password.length === 0) return 'empty';
  if (password.length < 8) return 'weak';
  if (password.length < 12) return 'medium';
  return 'strong';
};

const MASKED_TEXT_STYLE = {
  WebkitTextSecurity: 'disc',
} as React.CSSProperties;

const supportsTextSecurity = () =>
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('-webkit-text-security', 'disc');

const STRENGTH_SEGMENTS: Record<PasswordStrength, string[]> = {
  empty: ['bg-[#CBCCD7]', 'bg-[#CBCCD7]', 'bg-[#CBCCD7]'],
  weak: ['bg-[#F43F5E]', 'bg-[#CBCCD7]', 'bg-[#CBCCD7]'],
  medium: ['bg-[#F4AC3F]', 'bg-[#F4AC3F]', 'bg-[#CBCCD7]'],
  strong: ['bg-[#1BCF63]', 'bg-[#1BCF63]', 'bg-[#1BCF63]'],
};

const ProtectPdfField = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  type = 'password',
  inputMode,
  autoComplete,
  autoFocus,
  errorMessage,
  showStrength = false,
  disabled = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder: string;
  type?: 'password' | 'email';
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  autoFocus?: boolean;
  errorMessage?: string | null;
  showStrength?: boolean;
  disabled?: boolean;
}) => {
  const t = useTranslations('protect_pdf_flow');
  const [isVisible, setIsVisible] = React.useState(false);
  const isPassword = type === 'password';
  const isMasked = isPassword && !isVisible;
  const canMaskWithStyle = React.useMemo(supportsTextSecurity, []);
  const isInvalid = Boolean(errorMessage);
  const errorId = `${id}-error`;
  const strengthSegments = STRENGTH_SEGMENTS[getPasswordStrength(value)];

  const handleClipboard = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (isMasked) {
      event.preventDefault();
    }
  };

  const input = (
    <div className="relative h-12 w-full">
      <input
        id={id}
        type={isMasked && !canMaskWithStyle ? 'password' : 'text'}
        inputMode={inputMode}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        onCopy={handleClipboard}
        onCut={handleClipboard}
        placeholder={placeholder}
        autoComplete={isPassword ? 'off' : autoComplete}
        data-1p-ignore={isPassword ? true : undefined}
        data-lpignore={isPassword ? 'true' : undefined}
        data-bwignore={isPassword ? 'true' : undefined}
        data-form-type={isPassword ? 'other' : undefined}
        style={
          isMasked && canMaskWithStyle && value.length > 0
            ? MASKED_TEXT_STYLE
            : undefined
        }
        autoFocus={autoFocus}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        disabled={disabled}
        aria-invalid={isInvalid}
        aria-describedby={isInvalid ? errorId : undefined}
        className={clsx(
          'absolute inset-0 h-full w-full rounded-[5px] bg-transparent pl-4 font-barlow text-[15px] font-normal leading-[normal] text-[#454655] outline-none placeholder:text-[#787887] disabled:cursor-not-allowed',
          isPassword ? 'pr-11' : 'pr-4'
        )}
      />
      <div
        aria-hidden
        className={clsx(
          'pointer-events-none absolute inset-0 rounded-[5px] border-solid',
          isInvalid ? 'border-2 border-[#F43F5E]' : 'border border-[#CECFD4]'
        )}
      />
      {isPassword ? (
        <button
          type="button"
          aria-label={isVisible ? t('hide_password') : t('show_password')}
          aria-controls={id}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setIsVisible((visible) => !visible)}
          className="absolute right-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-[#787887] outline-none"
        >
          {isVisible ? (
            <EyeSlash className="h-5 w-5" />
          ) : (
            <EyeOpen className="h-5 w-5" />
          )}
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="flex w-full flex-col items-start gap-2">
      <label
        htmlFor={id}
        className="font-barlow text-[17px] font-semibold leading-[20px] text-[#454655]"
      >
        {label}
      </label>
      {showStrength ? (
        <div className="flex w-full flex-col items-start gap-1">
          {input}
          <div aria-hidden className="flex h-1 w-full items-start gap-1">
            {strengthSegments.map((segmentClassName, index) => (
              <div
                key={index}
                className={clsx(
                  'h-full min-w-px flex-1 rounded-[100px] transition-colors',
                  segmentClassName
                )}
              />
            ))}
          </div>
        </div>
      ) : (
        input
      )}
      {isInvalid ? (
        <span
          id={errorId}
          role="alert"
          className="font-barlow text-[13px] font-normal leading-[16px] text-[#F43F5E]"
        >
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
};

export default ProtectPdfField;
